import React, { useState } from "react";
import {
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  Avatar,
} from "@mui/material";
import { safeDateTime } from "../../../utils";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const getLogMeta = (type) => {
  switch (type) {
    case "add_user":
      return { icon: <PersonAddIcon fontSize='small' />, label: "Added User" };
    case "add_company":
      return {
        icon: <AddBusinessIcon fontSize='small' />,
        label: "Added Company",
      };
    case "upgrade":
      return { icon: <UpgradeIcon fontSize='small' />, label: "Plan Upgrade" };
    case "downgrade":
      return {
        icon: <ReceiptLongIcon fontSize='small' />,
        label: "Plan Downgrade",
      };
    default:
      return { icon: <EventNoteIcon fontSize='small' />, label: "Activity" };
  }
};

// 辅助函数：格式化金额（假设数据是美分）
const formatCurrency = (cents) => {
  if (!cents || cents === 0) return "Free / Included";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
};

const LOGS_STEP = 6;

const ChangeLogs = ({ subscriptionLogs }) => {
  const [visibleCount, setVisibleCount] = useState(LOGS_STEP);

  if (!subscriptionLogs) return null;

  const handleShowMore = () => {
    setVisibleCount((prev) =>
      Math.min(prev + LOGS_STEP, subscriptionLogs.length),
    );
  };

  const handleShowLess = () => {
    setVisibleCount((prev) => Math.max(LOGS_STEP, prev - LOGS_STEP));
  };

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        maxHeight: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          pr: 1,
          pb: 7, // reserve space for sticky footer
        }}
      >
        <Stack spacing={2}>
          {subscriptionLogs.slice(0, visibleCount).map((log, index, arr) => {
            const { icon, label } = getLogMeta(log.change_type);
            const isLast = index === arr.length - 1;

            return (
              <Box key={log.scl_id}>
                <Stack direction='row' spacing={2} alignItems='flex-start'>
                  {/* 左侧：带背景的图标 */}
                  <Avatar
                    sx={{ bgcolor: "primary.light", width: 36, height: 36 }}
                  >
                    {icon}
                  </Avatar>

                  {/* 右侧：详细内容 */}
                  <Stack spacing={0.5} sx={{ flex: 1 }}>
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      alignItems='center'
                    >
                      <Typography fontWeight={700} variant='body1'>
                        {label}
                      </Typography>
                      {/* 如果有费用，显示费用；如果是 Trial，显示 Trial 标签 */}
                      {log.status === "trial" ? (
                        <Chip
                          label='Trial'
                          size='small'
                          color='warning'
                          variant='outlined'
                          sx={{ height: 20, fontSize: "0.65rem" }}
                        />
                      ) : (
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          fontWeight={600}
                        >
                          {formatCurrency(log.charge_after_discount)}
                        </Typography>
                      )}
                    </Stack>

                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <AccessTimeIcon sx={{ fontSize: 12 }} />
                      Effective: {safeDateTime(log.effective_date)}
                    </Typography>

                    {/* 如果有折扣信息，可以额外展示 */}
                    {log.discount > 0 && (
                      <Typography variant='caption' color='success.main'>
                        Discount applied: {formatCurrency(log.discount)}
                      </Typography>
                    )}
                  </Stack>
                </Stack>

                {/* 如果不是最后一条，显示分割线 */}
                {!isLast && <Divider sx={{ mt: 2 }} />}
              </Box>
            );
          })}
        </Stack>
      </Box>
      {(visibleCount < subscriptionLogs.length || visibleCount > LOGS_STEP) && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(transparent, #fff 60%, #fff)", // fade for contrast
            textAlign: "center",
            py: 1.5,
          }}
        >
          {visibleCount < subscriptionLogs.length && (
            <Button
              size='small'
              onClick={handleShowMore}
              endIcon={<KeyboardArrowDownIcon />}
              sx={{
                textTransform: "none", // 取消全大写
                fontWeight: 600,
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
                mr: visibleCount > LOGS_STEP ? 1 : 0,
              }}
            >
              Show More
            </Button>
          )}
          {subscriptionLogs.length > LOGS_STEP && visibleCount > LOGS_STEP && (
            <Button
              size='small'
              onClick={handleShowLess}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              Show Less
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ChangeLogs;
